import type { ReactNode } from "react";
import type { Control, FieldPath, FieldValues } from "react-hook-form";

import {
  MultiUserPickerList,
  type MultiUserPickerOption,
} from "@/shared/components/multi-user-picker/multi-user-picker-list";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";

interface MultiUserFieldProps<TValues extends FieldValues> {
  control: Control<TValues>;
  name: FieldPath<TValues>;
  label: string;
  options: ReadonlyArray<MultiUserPickerOption>;
  /// Maximum selectable users. Picker stops accepting clicks past `max`.
  max?: number;
  /// Key that triggers the picker to refocus its search input. Pass the
  /// dialog open-key so the input focuses on each open.
  autoFocusKey?: string | number | boolean;
  /// Slot shown when `options` is empty (use to distinguish loading vs none).
  emptyState?: ReactNode;
  searchPlaceholder?: string;
  hint?: string;
  className?: string;
}

/// Multi-user picker wired to react-hook-form. Stores an array of userIds
/// in the field value. Mirrors kudos / todo bulk-reassign / 1:1 attendee
/// patterns.
export function MultiUserField<TValues extends FieldValues>({
  control,
  name,
  label,
  options,
  max,
  autoFocusKey,
  emptyState,
  searchPlaceholder,
  hint,
  className,
}: MultiUserFieldProps<TValues>): ReactNode {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const value = (field.value as ReadonlyArray<string> | undefined) ?? [];
        return (
          <FormItem className={className}>
            <FormLabel>{label}</FormLabel>
            {/* FormControl is a Slot: it puts the id, aria-describedby and
                aria-invalid on its immediate child, and <FormLabel htmlFor>
                points at that id. The framing div keeps its job, and
                FormControl moves inside it so the id reaches the picker, which
                forwards it to its search input. */}
            <div className="rounded-md border border-border bg-card">
              <FormControl>
                <MultiUserPickerList
                  value={value}
                  onChange={(next) => field.onChange([...next])}
                  options={options}
                  max={max}
                  autoFocusKey={autoFocusKey}
                  searchPlaceholder={searchPlaceholder}
                  emptyState={emptyState}
                />
              </FormControl>
            </div>
            {hint ? <FormDescription>{hint}</FormDescription> : null}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
